import React, { useState } from 'react';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarStatus = 'online' | 'offline' | 'busy' | 'idle';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  name?: string;
  initials?: string;
  color?: string;
  isBot?: boolean;
  size?: AvatarSize | number;
  status?: AvatarStatus;
  alt?: string;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#8b5cf6', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#6366f1', // indigo
  '#f97316'  // orange
];

const getInitials = (name?: string, fallback = '?'): string => {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getDeterministicColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % DEFAULT_COLORS.length;
  return DEFAULT_COLORS[index];
};

export const Avatar: React.FC<AvatarProps> = ({
  src,
  name = '',
  initials,
  color,
  isBot = false,
  size = 'md',
  status,
  alt,
  className = '',
  style,
  ...props
}) => {
  const [imageError, setImageError] = useState(false);

  const displayInitials = initials || (isBot ? '🤖' : getInitials(name));
  const bgColor = color || (isBot ? 'var(--z-surface-2)' : getDeterministicColor(name || 'user'));

  const sizePixels: Record<AvatarSize, number> = {
    xs: 20,
    sm: 26,
    md: 34,
    lg: 42,
    xl: 52
  };

  const px = typeof size === 'number' ? size : sizePixels[size] || sizePixels.md;
  const fontSize = Math.max(9, Math.round(px * 0.4));

  const avatarContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${px}px`,
    height: `${px}px`,
    borderRadius: isBot ? 'var(--z-radius-md, 8px)' : '50%',
    backgroundColor: bgColor,
    color: '#ffffff',
    fontWeight: 600,
    fontSize: `${fontSize}px`,
    fontFamily: 'var(--font-sans)',
    userSelect: 'none',
    overflow: 'hidden',
    flexShrink: 0,
    border: 'none',
    boxShadow: 'var(--z-sh-4)',
    ...style
  };

  const getStatusColor = (st: AvatarStatus) => {
    switch (st) {
      case 'online':
        return 'var(--z-ok)';
      case 'busy':
        return 'var(--z-bad)';
      case 'idle':
        return 'var(--z-warn)';
      case 'offline':
      default:
        return 'var(--z-muted)';
    }
  };

  const renderContent = () => {
    if (src && !imageError) {
      return (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          onError={() => setImageError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      );
    }

    if (isBot && !initials) {
      return (
        <span style={{ fontSize: `${Math.round(px * 0.55)}px` }} aria-hidden="true">
          🤖
        </span>
      );
    }

    return <span>{displayInitials}</span>;
  };

  return (
    <div
      className={`z-avatar z-avatar-${size} ${className}`}
      style={avatarContainerStyle}
      aria-label={alt || name || (isBot ? 'Bot' : 'Avatar')}
      role="img"
      {...props}
    >
      {renderContent()}
      {status && (
        <span
          style={{
            position: 'absolute',
            bottom: '0px',
            right: '0px',
            width: `${Math.max(6, Math.round(px * 0.28))}px`,
            height: `${Math.max(6, Math.round(px * 0.28))}px`,
            borderRadius: '50%',
            backgroundColor: getStatusColor(status),
            boxShadow: '0 0 0 2px var(--z-surface)',
            boxSizing: 'content-box'
          }}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  );
};
